/**
 * Job Hunt Dashboard — header, KPI strip, and recommended actions
 * (composed separately in DashboardOverview for F-pattern layout).
 */
import { ArrowRight, Briefcase, Calendar, CheckCircle2, FileText, Flame, Star } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface DailySnapshot {
  newMatchingJobs: number;
  strongMatches: number;
  applicationsSent: number;
  followUpsDue: number;
  interviews: number;
}

export interface RecommendedAction {
  id: string;
  icon: 'apply' | 'followup' | 'ats' | 'interview';
  text: string;
  onClick?: () => void;
}

function getGreetingKey(): string {
  const h = new Date().getHours();
  if (h < 12) return 'dashboard.greeting.morning';
  if (h < 17) return 'dashboard.greeting.afternoon';
  return 'dashboard.greeting.evening';
}

export function DashboardGreetingHeader({
  firstName,
  streakDays = 0,
}: {
  firstName: string | null;
  streakDays?: number;
}) {
  const { t } = useTranslation();
  const greetName = (firstName || '').trim() || t('dashboard.greeting.there', 'there');

  return (
    <header className="ds-dashboard-hero__greeting">
      <div>
        <h1 className="ds-dashboard-hero__title">
          {t(getGreetingKey(), { name: greetName, defaultValue: `Good morning, ${greetName}` })}
          <span className="ml-1.5" aria-hidden>👋</span>
        </h1>
        <p className="ds-dashboard-hero__subtitle">
          {t('dashboard.greeting.subtitle', 'Your job search today')}
        </p>
      </div>
      {streakDays > 0 ? (
        <div className="ds-dashboard-hero__streak">
          <Flame className="w-4 h-4" aria-hidden />
          <span className="t-num font-bold">{streakDays}</span>
          <span>{t('dashboard.greeting.streakDays', 'day streak')}</span>
        </div>
      ) : null}
    </header>
  );
}

export function DashboardKpiStrip({ snapshot }: { snapshot: DailySnapshot }) {
  const { t } = useTranslation();

  const widgets = [
    {
      key: 'apps',
      label: t('dashboard.greeting.kpiApplications', 'Total applications'),
      value: snapshot.applicationsSent,
      Icon: CheckCircle2,
      color: '#22c55e',
    },
    {
      key: 'interviews',
      label: t('dashboard.greeting.metricInterviews', 'Interviews'),
      value: snapshot.interviews,
      Icon: FileText,
      color: '#6366f1',
    },
    {
      key: 'followups',
      label: t('dashboard.greeting.metricFollowUps', 'Follow-ups due'),
      value: snapshot.followUpsDue,
      Icon: Calendar,
      color: '#f97316',
    },
    {
      key: 'newJobs',
      label: t('dashboard.greeting.kpiNewMatches', 'New matches'),
      value: snapshot.newMatchingJobs,
      Icon: Briefcase,
      color: 'var(--ds-marketing-accent, #5F8568)',
    },
  ];

  return (
    <div className="ds-dashboard-kpi-strip" role="list">
      {widgets.map((w) => (
        <div key={w.key} className="ds-dashboard-kpi-widget" role="listitem">
          <w.Icon className="ds-dashboard-kpi-widget__icon" style={{ color: w.color }} aria-hidden />
          <span className="ds-dashboard-kpi-widget__value t-num">{w.value}</span>
          <span className="ds-dashboard-kpi-widget__label">{w.label}</span>
        </div>
      ))}
    </div>
  );
}

export function DashboardRecommendedActions({ actions }: { actions: RecommendedAction[] }) {
  const { t } = useTranslation();
  if (actions.length === 0) return null;

  return (
    <section className="ds-dashboard-rec-actions" aria-labelledby="dashboard-rec-actions-title">
      <h2 id="dashboard-rec-actions-title" className="ds-dashboard-rec-actions__title">
        <span aria-hidden>🔥</span>
        {t('dashboard.greeting.recommendedActions', 'Recommended actions')}
      </h2>
      <ol className="ds-dashboard-rec-actions__list">
        {actions.map((action) => (
          <li key={action.id}>
            <button
              type="button"
              onClick={action.onClick}
              className="ds-dashboard-rec-actions__item touch-manipulation"
            >
              <span className="ds-dashboard-rec-actions__text">{action.text}</span>
              <ArrowRight className="w-3.5 h-3.5 shrink-0 opacity-40" aria-hidden />
            </button>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** @deprecated Use split header/kpi/actions components in DashboardOverview */
const DashboardGreeting: React.FC<{
  firstName: string | null;
  snapshot: DailySnapshot;
  actions: RecommendedAction[];
  streakDays?: number;
  onNavigate: (page: string) => void;
}> = ({ firstName, snapshot, actions, streakDays = 0 }) => (
  <section className="ds-job-hunt-card">
    <DashboardGreetingHeader firstName={firstName} streakDays={streakDays} />
    <DashboardKpiStrip snapshot={snapshot} />
    <DashboardRecommendedActions actions={actions} />
  </section>
);

export default React.memo(DashboardGreeting);
